import {
  Badge, Box, Tabs, Text,
} from '@usealloy/component-library';
import { PersonUpdatedOverview } from './PersonUpdatedOverview';
import type { CoreRiskData } from '../../RiskRatingCard/useRiskData';
import { RiskRatingCard } from '../../RiskRatingCard/RiskRatingCard';
import { useEventTypeEvaluationsContext } from '../context';
import { useContext, useMemo } from 'react';
import { UserContext } from '~/dashboard/context';
import { useGetWatchlistMatches } from '../../watchlist-review/hooks/getWatchlistMatches';
import { ReviewTabCount } from '../../watchlist-review/styles';
import { WatchlistMatches } from '../tabs';

interface PersonUpdatedTabsProps {
  canViewRiskFormula: boolean;
  riskData: CoreRiskData;
  shouldRenderRiskTab: boolean;
  shouldRenderWatchlistTab: boolean;
}

export const PersonUpdatedTabs = ({
  canViewRiskFormula, riskData, shouldRenderRiskTab, shouldRenderWatchlistTab,
}: PersonUpdatedTabsProps) => {
  const { evaluation } = useEventTypeEvaluationsContext();
  const {
    evaluation_token: evaluationToken,
    entity_token: entityToken,
  } = evaluation;
  const { typhonDomain } = useContext(UserContext);

  const [{
    data: { matches = [] },
    isLoading: isLoadingWatchlistMatches,
  }] = useGetWatchlistMatches({
    evaluations: [{
      evaluationToken,
      entityToken,
    }],
    typhonDomain,
    showLatestDecisions: true,
  });
  const {
    riskFactors, riskFormula, riskRangeValues, totalRisk, riskRatingColor, riskRatingLabel,
  } = riskData;

  const tabs = useMemo(() => {
    const reviewMatchesLength = matches
      ? matches
        .filter(({ status, is_suppressed }) => (status === 'Needs Review' && !is_suppressed))
        .length
      : 0;

    const allTabs: Array<{
      label: React.ReactNode;
      value: string;
      content: React.ReactNode;
    }> = [
      {
        label: 'Overview',
        value: 'overview',
        content: <PersonUpdatedOverview />,
      },
    ];

    if (shouldRenderWatchlistTab) {
      allTabs.push({
        label: (
          <Box direction="row" gap={1}>
            Watchlist Matches
            {!isLoadingWatchlistMatches
              && reviewMatchesLength > 0 && (
              <ReviewTabCount status="danger">
                <Box justifyContent="center" alignItems="center">
                  {reviewMatchesLength}
                </Box>
              </ReviewTabCount>
            )}
          </Box>
        ),
        value: 'watchlist',
        content: <WatchlistMatches evaluation={evaluation} />,
      });
    }

    if (shouldRenderRiskTab) {
      allTabs.push({
        label: (
          <Box>
            <Text style={{ color: 'var(--cl-text-secondary)' }}>Risk Score</Text>
            { riskRatingColor && riskRatingLabel && (
              <Badge outline backgroundColor={riskRatingColor}>{riskRatingLabel}</Badge>
            )}
          </Box>
        ),
        value: 'risk-score',
        content: (
          <RiskRatingCard
            canViewRiskFormula={canViewRiskFormula}
            entityToken={evaluation.entity_token}
            evaluationToken={evaluation.evaluation_token}
            evaluationType={evaluation.evaluation_type}
            riskFactors={riskFactors}
            riskOutputFormula={riskFormula}
            riskRanges={riskRangeValues}
            riskValue={totalRisk}
            riskValueColor={riskRatingColor}
            riskValueLabel={riskRatingLabel}
            variant="entityRisk"
            workflowToken={evaluation.application_token}
            workflowVersion={evaluation.public_version}
          />
        ),
      });
    }

    return allTabs;
  }, [
    canViewRiskFormula,
    evaluation,
    isLoadingWatchlistMatches,
    matches,
    riskFactors,
    riskFormula,
    riskRangeValues,
    riskRatingColor,
    riskRatingLabel,
    shouldRenderRiskTab,
    shouldRenderWatchlistTab,
    totalRisk,
  ]);

  return (
    <Tabs style={{ width: '100%' }} tabs={tabs} />
  );
};
